import type { MouseEvent } from 'react';
import { ArrowRight, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { TeeLink } from '@/components/ui/tee-link';
import { filterAssistantLinks } from '@/utils/assistantLinks';

interface AssistantLinksProps {
  /** Typed unknown on purpose: history messages may reach this unfiltered. */
  links: unknown;
  disabled?: boolean;
}

const LABEL_KEYS = {
  member: 'assistant.links.openMember',
  lead: 'assistant.links.openLead',
  ghl_contact: 'assistant.links.openInSmartlev',
} as const;

export function AssistantLinks({ links, disabled }: AssistantLinksProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // SECURITY: this render-time re-filter is the sole guard on the history path
  // (history DTOs are stored verbatim by useAssistantChat). Never remove or bypass it.
  const safe = filterAssistantLinks(links);
  if (safe.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2" data-testid="assistant-links">
      {safe.map((link) =>
        link.type === 'ghl_contact' ? (
          <TeeLink
            key={link.href}
            variant="secondary"
            size="s"
            href={link.href}
            external
            disabled={disabled}
            rightIcon={<ExternalLink className="h-3.5 w-3.5" />}
          >
            {t(LABEL_KEYS[link.type])}
          </TeeLink>
        ) : (
          <TeeLink
            key={link.href}
            variant="secondary"
            size="s"
            href={link.href}
            disabled={disabled}
            rightIcon={<ArrowRight className="h-3.5 w-3.5" />}
            onClick={(event: MouseEvent<HTMLAnchorElement>) => {
              event.preventDefault();
              navigate(link.href);
            }}
          >
            {t(LABEL_KEYS[link.type])}
          </TeeLink>
        ),
      )}
    </div>
  );
}
